import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Card, CardBody, Table, Thead, Tbody, Tr, Th, Td, TableContainer,
  Badge, HStack, IconButton, Button, useDisclosure, useToast,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton, FormControl, FormLabel, Input, Select, Switch,
  VStack, Text, InputGroup, InputRightElement, Tooltip, Avatar,
  Box, Checkbox, SimpleGrid,
} from '@chakra-ui/react';
import { AddIcon, ViewIcon, ViewOffIcon, EditIcon, DeleteIcon, ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { useTranslation } from 'react-i18next';
import PageShell from '../components/PageShell';
import ConfirmModal from '../components/ConfirmModal';
import staffService from '../services/staffService';
import apiClient from '../services/apiClient';

// labelKey is translated where the role is rendered; value is what the API stores.
const ROLES = [
  { value: 'admin', labelKey: 'staffManagement.roles.admin' },
  { value: 'manager', labelKey: 'staffManagement.roles.manager' },
  { value: 'sales', labelKey: 'staffManagement.roles.sales' },
  { value: 'finance_manager', labelKey: 'staffManagement.roles.financeManager' },
  { value: 'front_desk_agent', labelKey: 'staffManagement.roles.frontDeskAgent' },
  { value: 'housekeeper', labelKey: 'staffManagement.roles.housekeeper' },
  { value: 'maintenance', labelKey: 'staffManagement.roles.maintenance' },
];

const ROLE_COLORS = {
  admin: 'red', manager: 'purple', sales: 'green', finance_manager: 'orange',
  front_desk_agent: 'blue', housekeeper: 'teal', maintenance: 'gray',
};

// ---- Access permissions (custom roles) -------------------------------
// The backend catalog (/auth/access-catalog/) defines the modules, which
// capabilities each offers, and the default matrix per role. The role picked
// above stays the base template; any edited checkbox turns the user into a
// "custom role" saved as access_overrides on their profile.

const CAP_COLUMNS = ['view', 'add', 'edit', 'delete'];
const CAP_FALLBACK_LABELS = { view: 'View', add: 'Add', edit: 'Edit', delete: 'Delete' };

const cloneMatrix = (m) => JSON.parse(JSON.stringify(m || {}));

const matrixEquals = (a, b) => {
  if (!a || !b) return false;
  const mods = new Set([...Object.keys(a), ...Object.keys(b)]);
  for (const mod of mods) {
    const ca = a[mod] || {};
    const cb = b[mod] || {};
    const caps = new Set([...Object.keys(ca), ...Object.keys(cb)]);
    for (const cap of caps) {
      if (!!ca[cap] !== !!cb[cap]) return false;
    }
  }
  return true;
};

const AccessMatrixEditor = ({ catalog, value, onChange }) => {
  const { t } = useTranslation();
  const toggle = (moduleKey, cap) => {
    const next = cloneMatrix(value);
    const row = { ...(next[moduleKey] || {}) };
    const checked = !row[cap];
    row[cap] = checked;
    // A user who can add/edit/delete must be able to see the module; a user
    // stripped of View loses the write capabilities with it.
    if (checked && cap !== 'view' && cap !== 'use') row.view = true;
    if (!checked && cap === 'view') {
      ['add', 'edit', 'delete'].forEach((c) => { if (c in row) row[c] = false; });
    }
    next[moduleKey] = row;
    onChange(next);
  };

  const moduleRows = catalog.modules.filter((m) => m.group === 'module');
  const actionRows = catalog.modules.filter((m) => m.group === 'action');

  return (
    <Box>
      <TableContainer>
        <Table size="sm" variant="simple">
          <Thead>
            <Tr>
              <Th fontSize="xs" py={1}>{t('staffManagement.access.module', 'Module')}</Th>
              {CAP_COLUMNS.map((cap) => (
                <Th key={cap} fontSize="xs" py={1} textAlign="center">
                  {t(`staffManagement.access.caps.${cap}`, CAP_FALLBACK_LABELS[cap])}
                </Th>
              ))}
            </Tr>
          </Thead>
          <Tbody>
            {moduleRows.map((m) => (
              <Tr key={m.key}>
                <Td fontSize="xs" py={1}>{m.label}</Td>
                {CAP_COLUMNS.map((cap) => (
                  <Td key={cap} py={1} textAlign="center">
                    {m.caps.includes(cap) ? (
                      <Checkbox
                        size="sm"
                        isChecked={!!value?.[m.key]?.[cap]}
                        onChange={() => toggle(m.key, cap)}
                      />
                    ) : (
                      <Text as="span" fontSize="xs" color="gray.300">—</Text>
                    )}
                  </Td>
                ))}
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>
      <Text fontSize="xs" fontWeight="600" color="gray.600" mt={3} mb={1}>
        {t('staffManagement.access.specialActions', 'Special actions')}
      </Text>
      <SimpleGrid columns={2} spacingX={4} spacingY={1}>
        {actionRows.map((m) => (
          <Checkbox
            key={m.key}
            size="sm"
            isChecked={!!value?.[m.key]?.use}
            onChange={() => toggle(m.key, 'use')}
          >
            <Text as="span" fontSize="xs">{m.label}</Text>
          </Checkbox>
        ))}
      </SimpleGrid>
    </Box>
  );
};

const emptyForm = {
  username: '', email: '', first_name: '', last_name: '', password: '', confirm_password: '',
  role: 'front_desk_agent', department: '', employee_id: '', phone: '',
};

const PAGE_SIZE = 20;

const StaffManagement = () => {
  const { t } = useTranslation();
  const [staff, setStaff] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  // Access-permission matrix being edited in the modal. null catalog means
  // the backend doesn't expose /auth/access-catalog/ yet — the section hides
  // and the page behaves exactly as before.
  const [catalog, setCatalog] = useState(null);
  const [access, setAccess] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const deleteDisclosure = useDisclosure();
  const toast = useToast();

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  const loadStaff = useCallback(async () => {
    try {
      setLoading(true);
      const data = await staffService.getAll({ page, page_size: PAGE_SIZE });
      if (data?.results) {
        setStaff(data.results);
        setTotalCount(data.count || 0);
      } else {
        const list = Array.isArray(data) ? data : [];
        setStaff(list);
        setTotalCount(list.length);
      }
    } catch (err) {
      setError(t('staffManagement.errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [page, t]);

  useEffect(() => { loadStaff(); }, [loadStaff]);

  useEffect(() => {
    staffService.getAccessCatalog().then(setCatalog).catch(() => setCatalog(null));
  }, []);

  const roleDefaultsFor = useCallback(
    (role) => cloneMatrix(catalog?.role_defaults?.[role] || {}),
    [catalog],
  );

  // Any checkbox differing from the role's defaults = custom role.
  const isCustomAccess = useMemo(() => {
    if (!catalog || !access) return false;
    return !matrixEquals(access, catalog.role_defaults?.[form.role] || {});
  }, [catalog, access, form.role]);

  const openCreate = () => {
    setForm(emptyForm);
    setAccess(roleDefaultsFor(emptyForm.role));
    setEditingId(null);
    onOpen();
  };

  const openEdit = (user) => {
    setForm({
      username: user.username,
      email: user.email,
      first_name: user.first_name,
      last_name: user.last_name,
      password: '',
      confirm_password: '',
      role: user.role || user.profile?.role || 'front_desk_agent',
      department: user.department ?? user.profile?.department ?? '',
      employee_id: user.employee_id ?? user.profile?.employee_id ?? '',
      phone: user.phone ?? user.profile?.phone ?? '',
    });
    setAccess(
      user.effective_access
        ? cloneMatrix(user.effective_access)
        : roleDefaultsFor(user.role || user.profile?.role || 'front_desk_agent'),
    );
    setEditingId(user.id);
    onOpen();
  };

  // Changing the role re-fills the matrix with that role's defaults —
  // the role is the template, the checkboxes are the customisation.
  const handleRoleChange = (e) => {
    const role = e.target.value;
    setForm((f) => ({ ...f, role }));
    if (catalog) setAccess(roleDefaultsFor(role));
  };

  const handleCopyFrom = (e) => {
    const id = Number(e.target.value);
    if (!id) return;
    const source = staff.find((u) => u.id === id);
    if (source?.effective_access) {
      setAccess(cloneMatrix(source.effective_access));
      toast({
        title: t('staffManagement.toasts.permissionsCopied', { name: source.first_name || source.username }),
        status: 'info',
        duration: 2000,
      });
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editingId) {
        // Send every field the form collects. Previously only name/email
        // went up, so role, department, employee_id and phone were edited in
        // the UI and silently discarded — the toast said "Staff updated"
        // while nothing changed.
        await staffService.update(editingId, {
          first_name: form.first_name,
          last_name: form.last_name,
          email: form.email,
          role: form.role,
          department: form.department,
          employee_id: form.employee_id,
          phone: form.phone,
          // {} = pure role defaults; a full matrix = custom role.
          ...(catalog ? { access_overrides: isCustomAccess ? access : {} } : {}),
        });
      } else {
        if (form.password !== form.confirm_password) {
          toast({ title: t('staffManagement.toasts.passwordsMismatch'), status: 'error', duration: 3000 });
          setSaving(false);
          return;
        }
        const hotelId = apiClient.getHotelId();
        await staffService.create({
          username: form.username,
          email: form.email,
          password: form.password,
          confirm_password: form.confirm_password,
          first_name: form.first_name,
          last_name: form.last_name,
          profile: {
            role: form.role,
            department: form.department,
            employee_id: form.employee_id,
            phone: form.phone,
            hotel: hotelId ? Number(hotelId) : undefined,
            ...(catalog && isCustomAccess ? { access_overrides: access } : {}),
          },
        });
      }
      toast({ title: editingId ? t('staffManagement.toasts.updated') : t('staffManagement.toasts.created'), status: 'success', duration: 2000 });
      onClose();
      loadStaff();
    } catch (err) {
      const msg = err.response?.data;
      toast({
        title: t('staffManagement.toasts.error'),
        description: typeof msg === 'string' ? msg : JSON.stringify(msg),
        status: 'error',
        duration: 4000,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (user) => {
    try {
      await staffService.setActive(user.id, !user.is_active);
      toast({ title: user.is_active
        ? t('staffManagement.toasts.deactivated', { username: user.username })
        : t('staffManagement.toasts.activated', { username: user.username }), status: 'info', duration: 2000 });
      loadStaff();
    } catch {
      toast({ title: t('staffManagement.toasts.statusUpdateFailed'), status: 'error', duration: 3000 });
    }
  };

  const handleDelete = async () => {
    try {
      await staffService.remove(deleteTarget.id);
      toast({ title: t('staffManagement.toasts.removed'), status: 'success', duration: 2000 });
      deleteDisclosure.onClose();
      loadStaff();
    } catch {
      toast({ title: t('staffManagement.toasts.removeFailed'), status: 'error', duration: 3000 });
    }
  };

  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  return (
    <PageShell
      title={t('staffManagement.title')}
      subtitle={t('staffManagement.subtitle', { count: totalCount })}
      breadcrumbs={[{ label: t('staffManagement.title') }]}
      loading={loading}
      error={error}
      actions={
        <Button leftIcon={<AddIcon />} colorScheme="blue" size="sm" onClick={openCreate}>
          {t('staffManagement.buttons.addStaff')}
        </Button>
      }
    >
      <Card shadow="sm" borderWidth="1px">
        <CardBody p={0}>
          <TableContainer>
            <Table size="sm" variant="simple">
              <Thead>
                <Tr>
                  <Th>{t('staffManagement.columns.name')}</Th>
                  <Th>{t('staffManagement.columns.username')}</Th>
                  <Th>{t('staffManagement.columns.email')}</Th>
                  <Th>{t('staffManagement.columns.role')}</Th>
                  <Th>{t('staffManagement.columns.department')}</Th>
                  <Th>{t('staffManagement.columns.status')}</Th>
                  <Th>{t('staffManagement.columns.lastLogin')}</Th>
                  <Th>{t('staffManagement.columns.actions')}</Th>
                </Tr>
              </Thead>
              <Tbody>
                {staff.map((u) => (
                  <Tr key={u.id}>
                    <Td>
                      <HStack>
                        <Avatar size="xs" name={`${u.first_name} ${u.last_name}`} />
                        <Text fontSize="sm" fontWeight="500">{u.first_name} {u.last_name}</Text>
                      </HStack>
                    </Td>
                    <Td fontSize="sm">{u.username}</Td>
                    <Td fontSize="sm">{u.email}</Td>
                    <Td>
                      <Badge colorScheme={ROLE_COLORS[u.role || u.profile?.role] || 'gray'} fontSize="xs">
                        {(() => {
                          const role = ROLES.find(r => r.value === (u.role || u.profile?.role));
                          return role ? t(role.labelKey) : (u.role || u.profile?.role || '-');
                        })()}
                      </Badge>
                      {u.access_overrides && Object.keys(u.access_overrides).length > 0 && (
                        <Badge ml={1} colorScheme="purple" variant="outline" fontSize="xs">
                          {t('staffManagement.access.customBadge', 'Custom')}
                        </Badge>
                      )}
                    </Td>
                    <Td fontSize="sm">{u.department || u.profile?.department || '-'}</Td>
                    <Td>
                      <Badge colorScheme={u.is_active ? 'green' : 'red'} fontSize="xs">
                        {u.is_active ? t('staffManagement.status.active') : t('staffManagement.status.inactive')}
                      </Badge>
                    </Td>
                    <Td fontSize="xs" color="gray.500">
                      {u.last_login ? new Date(u.last_login).toLocaleDateString() : t('staffManagement.status.never')}
                    </Td>
                    <Td>
                      <HStack spacing={1}>
                        <Tooltip label={t('staffManagement.actions.edit')}>
                          <IconButton size="xs" icon={<EditIcon />} variant="ghost" onClick={() => openEdit(u)} aria-label={t('staffManagement.actions.edit')} />
                        </Tooltip>
                        <Tooltip label={u.is_active ? t('staffManagement.actions.deactivate') : t('staffManagement.actions.activate')}>
                          <IconButton
                            size="xs"
                            icon={u.is_active ? <ViewOffIcon /> : <ViewIcon />}
                            variant="ghost"
                            colorScheme={u.is_active ? 'orange' : 'green'}
                            onClick={() => handleToggleActive(u)}
                            aria-label={t('staffManagement.actions.toggleActive')}
                          />
                        </Tooltip>
                        <Tooltip label={t('staffManagement.actions.delete')}>
                          <IconButton
                            size="xs" icon={<DeleteIcon />} variant="ghost" colorScheme="red"
                            onClick={() => { setDeleteTarget(u); deleteDisclosure.onOpen(); }}
                            aria-label={t('staffManagement.actions.delete')}
                          />
                        </Tooltip>
                      </HStack>
                    </Td>
                  </Tr>
                ))}
                {staff.length === 0 && (
                  <Tr><Td colSpan={8} textAlign="center" py={8} color="gray.400">{t('staffManagement.emptyState.noStaff')}</Td></Tr>
                )}
              </Tbody>
            </Table>
          </TableContainer>
        </CardBody>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <HStack justify="space-between" mt={4}>
          <Text fontSize="sm" color="gray.500">
            {t('staffManagement.pagination.showing', { from: (page - 1) * PAGE_SIZE + 1, to: Math.min(page * PAGE_SIZE, totalCount), total: totalCount })}
          </Text>
          <HStack spacing={2}>
            <IconButton size="sm" icon={<ChevronLeftIcon />} variant="outline"
              isDisabled={page <= 1} onClick={() => setPage(p => p - 1)} aria-label={t('staffManagement.pagination.previous')} />
            <Text fontSize="sm" fontWeight="500">{t('staffManagement.pagination.pageOf', { page, totalPages })}</Text>
            <IconButton size="sm" icon={<ChevronRightIcon />} variant="outline"
              isDisabled={page >= totalPages} onClick={() => setPage(p => p + 1)} aria-label={t('staffManagement.pagination.next')} />
          </HStack>
        </HStack>
      )}

      {/* Create / Edit Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{editingId ? t('staffManagement.modal.editTitle') : t('staffManagement.modal.addTitle')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <HStack w="100%" spacing={4}>
                <FormControl isRequired>
                  <FormLabel fontSize="sm">{t('staffManagement.form.firstName')}</FormLabel>
                  <Input size="sm" value={form.first_name} onChange={set('first_name')} />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel fontSize="sm">{t('staffManagement.form.lastName')}</FormLabel>
                  <Input size="sm" value={form.last_name} onChange={set('last_name')} />
                </FormControl>
              </HStack>

              <HStack w="100%" spacing={4}>
                <FormControl isRequired isDisabled={!!editingId}>
                  <FormLabel fontSize="sm">{t('staffManagement.form.username')}</FormLabel>
                  <Input size="sm" value={form.username} onChange={set('username')} />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel fontSize="sm">{t('staffManagement.form.email')}</FormLabel>
                  <Input size="sm" type="email" value={form.email} onChange={set('email')} />
                </FormControl>
              </HStack>

              {!editingId && (
                <HStack w="100%" spacing={4}>
                  <FormControl isRequired>
                    <FormLabel fontSize="sm">{t('staffManagement.form.password')}</FormLabel>
                    <InputGroup size="sm">
                      <Input type={showPassword ? 'text' : 'password'} value={form.password} onChange={set('password')} />
                      <InputRightElement>
                        <IconButton
                          size="xs" variant="ghost"
                          icon={showPassword ? <ViewOffIcon /> : <ViewIcon />}
                          onClick={() => setShowPassword(!showPassword)}
                          aria-label={t('staffManagement.form.togglePasswordVisibility')}
                        />
                      </InputRightElement>
                    </InputGroup>
                  </FormControl>
                  <FormControl isRequired>
                    <FormLabel fontSize="sm">{t('staffManagement.form.confirmPassword')}</FormLabel>
                    <Input size="sm" type="password" value={form.confirm_password} onChange={set('confirm_password')} />
                  </FormControl>
                </HStack>
              )}

              <HStack w="100%" spacing={4}>
                <FormControl isRequired>
                  <FormLabel fontSize="sm">{t('staffManagement.form.role')}</FormLabel>
                  {/* Editable on update now that the API accepts a role
                      change; username stays locked because it is the login. */}
                  <Select size="sm" value={form.role} onChange={handleRoleChange}>
                    {ROLES.map(r => <option key={r.value} value={r.value}>{t(r.labelKey)}</option>)}
                  </Select>
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">{t('staffManagement.form.department')}</FormLabel>
                  <Input size="sm" value={form.department} onChange={set('department')} />
                </FormControl>
              </HStack>

              <HStack w="100%" spacing={4}>
                <FormControl>
                  <FormLabel fontSize="sm">{t('staffManagement.form.employeeId')}</FormLabel>
                  <Input size="sm" value={form.employee_id} onChange={set('employee_id')} />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">{t('staffManagement.form.phone')}</FormLabel>
                  <Input size="sm" value={form.phone} onChange={set('phone')} />
                </FormControl>
              </HStack>

              {catalog && access && (
                <Box w="100%" borderWidth="1px" borderRadius="md" p={3}>
                  <HStack justify="space-between" mb={2} flexWrap="wrap" gap={2}>
                    <HStack>
                      <Text fontSize="sm" fontWeight="600">
                        {t('staffManagement.access.title', 'Access permissions')}
                      </Text>
                      <Badge colorScheme={isCustomAccess ? 'purple' : 'gray'} fontSize="xs">
                        {isCustomAccess
                          ? t('staffManagement.access.customRole', 'Custom role')
                          : t('staffManagement.access.roleDefaults', 'Role defaults')}
                      </Badge>
                    </HStack>
                    <HStack>
                      <Select
                        size="xs"
                        w="200px"
                        placeholder={t('staffManagement.access.copyFrom', 'Copy permissions from…')}
                        value=""
                        onChange={handleCopyFrom}
                      >
                        {staff
                          .filter((u) => u.id !== editingId && u.effective_access)
                          .map((u) => (
                            <option key={u.id} value={u.id}>
                              {(u.first_name || u.last_name)
                                ? `${u.first_name} ${u.last_name}`.trim()
                                : u.username}
                            </option>
                          ))}
                      </Select>
                      <Button size="xs" variant="ghost" onClick={() => setAccess(roleDefaultsFor(form.role))}>
                        {t('staffManagement.access.resetToRole', 'Reset to role')}
                      </Button>
                    </HStack>
                  </HStack>
                  <AccessMatrixEditor catalog={catalog} value={access} onChange={setAccess} />
                  <Text fontSize="xs" color="gray.500" mt={2}>
                    {t('staffManagement.access.hint',
                      'The role fills in default permissions; change any checkbox to give this user a custom role. View = read-only. Add / Edit / Delete are enforced by the server for custom roles.')}
                  </Text>
                </Box>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>{t('staffManagement.buttons.cancel')}</Button>
            <Button colorScheme="blue" onClick={handleSave} isLoading={saving}>
              {editingId ? t('staffManagement.buttons.saveChanges') : t('staffManagement.buttons.createStaff')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete confirmation */}
      <ConfirmModal
        isOpen={deleteDisclosure.isOpen}
        onClose={deleteDisclosure.onClose}
        onConfirm={handleDelete}
        title={t('staffManagement.removeConfirm.title')}
        message={t('staffManagement.removeConfirm.message', { name: `${deleteTarget?.first_name} ${deleteTarget?.last_name}` })}
        confirmLabel={t('staffManagement.removeConfirm.confirm')}
      />
    </PageShell>
  );
};

export default StaffManagement;
